import React, { useState, useEffect, useRef } from 'react';
import { api, formatDateTime, getInitials } from '../api';

const STATUS_ORDER = ['terkirim', 'ditinjau', 'diproses', 'selesai'];
const STATUS_LABEL = { terkirim: 'Terkirim', ditinjau: 'Ditinjau', diproses: 'Diproses', selesai: 'Selesai' };
const STATUS_COLOR = { terkirim: 'badge-gold', ditinjau: 'badge-blue', diproses: 'badge-yellow', selesai: 'badge-green' };
const STATUS_ICON = { terkirim: '📥', ditinjau: '🔍', diproses: '⚙️', selesai: '✅' };
const KATEGORI = ['umum', 'insiden', 'kerusakan', 'kehilangan', 'lainnya'];

function formatSLA(menit) {
  if (!menit) return '-';
  if (menit < 60) return `${menit} menit`;
  const jam = Math.floor(menit / 60);
  const sisa = menit % 60;
  return sisa > 0 ? `${jam} jam ${sisa} menit` : `${jam} jam`;
}

// Komponen lightbox foto
function FotoLightbox({ src, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: 12, objectFit: 'contain' }} />
      <button onClick={onClose} style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: 40, height: 40, borderRadius: '50%', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
    </div>
  );
}

export function LaporanPage({ user }) {
  const [laporan, setLaporan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [komentar, setKomentar] = useState([]);
  const [newKomen, setNewKomen] = useState('');
  const [form, setForm] = useState({ judul: '', isi: '', kategori: 'umum' });
  const [fotos, setFotos] = useState([]);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [periode, setPeriode] = useState('harian');
  const [tanggal, setTanggal] = useState(new Date().toISOString().split('T')[0]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState('');
  const [statusCatatan, setStatusCatatan] = useState('');
  const [statusFoto, setStatusFoto] = useState(null);
  const [statusFotoPreview, setStatusFotoPreview] = useState(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraMode, setCameraMode] = useState(false);
  const printRef = useRef(null);

  useEffect(() => { fetchLaporan(); }, [periode, tanggal]);
  useEffect(() => { if (selected) { fetchDetail(selected.id); fetchKomentar(selected.id); } }, [selected]);

  const fetchLaporan = async () => {
    setLoading(true);
    try {
      let params = '';
      if (periode === 'harian') params = `?tanggal=${tanggal}`;
      else if (periode === 'minggu') params = `?periode=minggu`;
      else if (periode === 'bulan') params = `?periode=bulan`;
      setLaporan(await api.get(`/laporan${params}`));
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setLoading(false); }
  };

  const fetchDetail = async (id) => {
    try {
      const data = await api.get(`/laporan/${id}`);
      setSelectedDetail(data);
    } catch {}
  };

  const fetchKomentar = async (id) => {
    try {
      const data = await api.get(`/komentar/laporan/${id}`);
      setKomentar(data);
    } catch {}
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const submitLaporan = async () => {
    if (!form.judul || !form.isi) { showMsg('Judul dan isi wajib diisi', 'error'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fotos.forEach(f => fd.append('foto', f));
      await api.postForm('/laporan', fd);
      showMsg('Laporan berhasil dikirim');
      setShowForm(false);
      setForm({ judul: '', isi: '', kategori: 'umum' });
      setFotos([]);
      fetchLaporan();
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const startStatusChange = (newStatus) => {
    setStatusTarget(newStatus);
    setStatusCatatan('');
    setStatusFoto(null);
    setStatusFotoPreview(null);
    setShowStatusModal(true);
  };

  const startCamera = async () => {
    setCameraMode(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { showMsg('Kamera tidak tersedia', 'error'); setCameraMode(false); }
  };

  const capturePhoto = () => {
    const canvas = document.createElement('canvas');
    const now = new Date();
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);

    // Overlay timestamp
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fillRect(0, canvas.height - 48, canvas.width, 48);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 20px Arial';
    ctx.fillText(now.toLocaleString('id-ID'), 10, canvas.height - 16);

    canvas.toBlob(blob => {
      const file = new File([blob], 'bukti.jpg', { type: 'image/jpeg' });
      setStatusFoto(file);
      setStatusFotoPreview(URL.createObjectURL(blob));
      stopCamera();
    }, 'image/jpeg', 0.9);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    setCameraMode(false);
  };

  const handleStatusFotoFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setStatusFoto(file);
      setStatusFotoPreview(URL.createObjectURL(file));
    }
  };

  const submitStatusChange = async () => {
    if (['diproses', 'selesai'].includes(statusTarget) && !statusFoto) {
      showMsg('Foto bukti wajib untuk status ini', 'error'); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('status', statusTarget);
      if (statusCatatan) fd.append('catatan', statusCatatan);
      if (statusFoto) fd.append('foto', statusFoto);
      await api.putForm(`/laporan/${selected.id}/status`, fd);
      showMsg('Status berhasil diupdate');
      setShowStatusModal(false);
      fetchLaporan();
      fetchDetail(selected.id);
      setSelected(prev => ({ ...prev, status: statusTarget }));
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const sendKomen = async () => {
    if (!newKomen.trim()) return;
    try {
      await api.post(`/komentar/laporan/${selected.id}`, { isi: newKomen });
      setNewKomen('');
      fetchKomentar(selected.id);
    } catch {}
  };

  // Download PDF
  const downloadPDF = (laporanData) => {
    const detail = laporanData || selectedDetail;
    if (!detail) return;

    const fotoUrls = Array.isArray(detail.foto_urls) ? detail.foto_urls : JSON.parse(detail.foto_urls || '[]');
    const historyHtml = (detail.history || []).map(h => `
      <tr>
        <td>${new Date(h.created_at).toLocaleString('id-ID')}</td>
        <td>${h.status_from || '-'} → ${h.status_to}</td>
        <td>${h.user_name}</td>
        <td>${h.catatan || '-'}</td>
        ${h.foto_url ? `<td><img src="${h.foto_url}" style="width:80px;height:60px;object-fit:cover;border-radius:4px"/></td>` : '<td>-</td>'}
      </tr>
    `).join('');

    const slaText = detail.sla_menit ? formatSLA(detail.sla_menit) : 'Belum selesai';

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Laporan - ${detail.judul}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 30px; color: #1a1a1a; font-size: 13px; }
          .header { display: flex; align-items: center; gap: 16px; border-bottom: 3px solid #c9a84c; padding-bottom: 16px; margin-bottom: 20px; }
          .logo { font-size: 28px; font-weight: 900; color: #0b1829; }
          .logo span { color: #c9a84c; }
          h1 { font-size: 18px; margin: 0 0 4px; }
          .meta { color: #666; font-size: 12px; }
          .section { margin: 16px 0; }
          .section-title { font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #666; border-bottom: 1px solid #eee; padding-bottom: 4px; margin-bottom: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .info-item { background: #f9f9f9; padding: 8px 12px; border-radius: 6px; }
          .info-label { font-size: 11px; color: #888; text-transform: uppercase; }
          .info-value { font-weight: 600; margin-top: 2px; }
          .status-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; background: #e8f5e9; color: #2e7d32; }
          .foto-grid { display: flex; gap: 8px; flex-wrap: wrap; margin: 8px 0; }
          .foto-grid img { width: 100px; height: 80px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th { background: #0b1829; color: #c9a84c; padding: 8px; text-align: left; font-size: 11px; }
          td { padding: 7px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
          tr:nth-child(even) td { background: #fafafa; }
          .sla-box { background: linear-gradient(135deg, #0b1829, #1a3354); color: #c9a84c; padding: 12px 16px; border-radius: 8px; margin: 12px 0; }
          .sla-box .label { font-size: 11px; text-transform: uppercase; opacity: 0.7; }
          .sla-box .value { font-size: 20px; font-weight: 800; margin-top: 4px; }
          .accountability { font-size: 12px; color: #444; margin: 4px 0; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="logo"><span>MIG</span> Security</div>
            <div class="meta">PT. Muhammad Ibrahim Group</div>
          </div>
          <div style="margin-left:auto;text-align:right">
            <div style="font-size:11px;color:#888">Dicetak pada</div>
            <div style="font-weight:600">${new Date().toLocaleString('id-ID')}</div>
          </div>
        </div>

        <h1>${detail.judul}</h1>
        <div class="meta">${detail.wilayah_nama} · ${new Date(detail.created_at).toLocaleString('id-ID')}</div>

        <div class="section">
          <div class="section-title">Informasi Laporan</div>
          <div class="info-grid">
            <div class="info-item">
              <div class="info-label">Pembuat</div>
              <div class="info-value">${detail.pembuat_name}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Kategori</div>
              <div class="info-value">${detail.kategori}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Wilayah</div>
              <div class="info-value">${detail.wilayah_nama}</div>
            </div>
            <div class="info-item">
              <div class="info-label">Status</div>
              <div class="info-value"><span class="status-badge">${detail.status}</span></div>
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Isi Laporan</div>
          <p style="line-height:1.7;margin:0">${detail.isi}</p>
        </div>

        ${fotoUrls.length > 0 ? `
        <div class="section">
          <div class="section-title">Foto Laporan Awal</div>
          <div class="foto-grid">
            ${fotoUrls.map(url => `<img src="${url}" />`).join('')}
          </div>
        </div>` : ''}

        <div class="sla-box">
          <div class="label">SLA (Waktu Penyelesaian)</div>
          <div class="value">${slaText}</div>
          ${detail.selesai_by_name ? `<div class="accountability" style="color:#e8c96a;margin-top:6px">✅ Diselesaikan oleh: <strong>${detail.selesai_by_name}</strong> pada ${new Date(detail.status_selesai_at).toLocaleString('id-ID')}</div>` : ''}
          ${detail.diproses_by_name ? `<div class="accountability" style="color:#e8c96a">⚙️ Diproses oleh: <strong>${detail.diproses_by_name}</strong> pada ${new Date(detail.status_diproses_at).toLocaleString('id-ID')}</div>` : ''}
          ${detail.ditinjau_by_name ? `<div class="accountability" style="color:#e8c96a">🔍 Ditinjau oleh: <strong>${detail.ditinjau_by_name}</strong> pada ${new Date(detail.status_ditinjau_at).toLocaleString('id-ID')}</div>` : ''}
        </div>

        ${(detail.history || []).length > 0 ? `
        <div class="section">
          <div class="section-title">Riwayat Penanganan</div>
          <table>
            <thead>
              <tr>
                <th>Waktu</th>
                <th>Perubahan Status</th>
                <th>Oleh</th>
                <th>Catatan</th>
                <th>Foto Bukti</th>
              </tr>
            </thead>
            <tbody>${historyHtml}</tbody>
          </table>
        </div>` : ''}

        <div style="margin-top:40px;border-top:1px solid #eee;padding-top:12px;font-size:11px;color:#888;text-align:center">
          Dokumen ini dibuat otomatis oleh sistem MIG Security · PT. Muhammad Ibrahim Group · mig-security.id
        </div>
      </body>
      </html>
    `;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.onload = () => {
      win.focus();
      win.print();
    };
  };

  // Download CSV (admin only)
  const downloadCSV = () => {
    const headers = ['Tanggal', 'Judul', 'Kategori', 'Pembuat', 'Wilayah', 'Status', 'SLA', 'Ditinjau Oleh', 'Diproses Oleh', 'Diselesaikan Oleh'];
    const rows = laporan.map(l => [
      new Date(l.created_at).toLocaleString('id-ID'),
      `"${l.judul}"`,
      l.kategori,
      l.pembuat_name,
      l.wilayah_nama,
      l.status,
      l.sla_menit ? formatSLA(l.sla_menit) : 'Belum selesai',
      l.ditinjau_by_name || '-',
      l.diproses_by_name || '-',
      l.selesai_by_name || '-',
    ]);

    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `laporan_mig_${periode}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const nextStatus = (current) => {
    const idx = STATUS_ORDER.indexOf(current);
    return idx < STATUS_ORDER.length - 1 ? STATUS_ORDER[idx + 1] : null;
  };

  return (
    <div className="page fade-in">
      {lightbox && <FotoLightbox src={lightbox} onClose={() => setLightbox(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h1 className="page-title">Laporan</h1>
          <p className="page-subtitle">Laporan kejadian & insiden</p>
        </div>
        <button className="btn btn-gold btn-sm" onClick={() => setShowForm(true)}>+ Buat</button>
      </div>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {/* Filter Periode */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="chip-group" style={{ flex: 1 }}>
          {['harian', 'minggu', 'bulan'].map(p => (
            <button key={p} className={`chip ${periode === p ? 'active' : ''}`} onClick={() => setPeriode(p)}>
              {p === 'harian' ? '📅 Harian' : p === 'minggu' ? '📆 Minggu Ini' : '🗓️ Bulan Ini'}
            </button>
          ))}
        </div>
        {periode === 'harian' && (
          <input type="date" className="input" style={{ width: 150 }} value={tanggal} onChange={e => setTanggal(e.target.value)} />
        )}
      </div>

      {/* Tombol Download */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {user.role === 'admin' && (
          <button className="btn btn-outline btn-sm" onClick={downloadCSV} disabled={laporan.length === 0}>
            📊 CSV
          </button>
        )}
        <button className="btn btn-outline btn-sm" onClick={() => {
          const periodeLabel = periode === 'harian' ? `Harian ${tanggal}` : periode === 'minggu' ? 'Minggu Ini' : 'Bulan Ini';
          const rows = laporan.map(l => `<tr>
            <td>${new Date(l.created_at).toLocaleDateString('id-ID')}</td>
            <td>${l.judul}</td>
            <td>${l.kategori}</td>
            <td>${l.pembuat_name}</td>
            <td>${l.wilayah_nama}</td>
            <td style="text-align:center"><span style="padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;background:${l.status==='selesai'?'#e8f5e9':l.status==='diproses'?'#fff3e0':'#e3f2fd'};color:${l.status==='selesai'?'#2e7d32':l.status==='diproses'?'#e65100':'#1565c0'}">${l.status}</span></td>
            <td style="text-align:center">${l.sla_menit ? Math.floor(l.sla_menit/60)+'j '+l.sla_menit%60+'m' : '-'}</td>
            <td>${l.selesai_by_name || '-'}</td>
          </tr>`).join('');
          const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Laporan Kejadian ${periodeLabel}</title>
          <style>body{font-family:Arial,sans-serif;margin:30px;font-size:12px}table{width:100%;border-collapse:collapse}th{background:#0b1829;color:#c9a84c;padding:8px;font-size:11px;text-align:left}td{padding:6px 8px;border-bottom:1px solid #eee;vertical-align:top}.logo span{color:#c9a84c}@media print{body{margin:0}}</style></head>
          <body><div style="display:flex;justify-content:space-between;border-bottom:3px solid #c9a84c;padding-bottom:12px;margin-bottom:20px">
          <div><div style="font-size:20px;font-weight:900"><span style="color:#c9a84c">MIG</span> Security</div><div style="font-size:11px;color:#888">PT. Muhammad Ibrahim Group</div></div>
          <div style="text-align:right"><div style="font-weight:700">Laporan Kejadian — ${periodeLabel}</div><div style="font-size:11px;color:#888">Total: ${laporan.length} laporan · Dicetak: ${new Date().toLocaleString('id-ID')}</div></div></div>
          <table><thead><tr><th>Tanggal</th><th>Judul</th><th>Kategori</th><th>Pembuat</th><th>Wilayah</th><th>Status</th><th>SLA</th><th>Diselesaikan Oleh</th></tr></thead>
          <tbody>${rows}</tbody></table>
          <div style="margin-top:40px;border-top:1px solid #eee;padding-top:10px;font-size:10px;color:#aaa;text-align:center">MIG Security System · mig-security.id</div>
          </body></html>`;
          const win = window.open('', '_blank'); win.document.write(html); win.document.close(); win.onload = () => { win.focus(); win.print(); };
        }} disabled={laporan.length === 0}>
          🖨️ PDF
        </button>
      </div>

      {/* List Laporan */}
      {loading ? <div className="loading-center"><div className="loading-spinner" /></div> :
        laporan.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p>Belum ada laporan pada periode ini</p>
          </div>
        ) : laporan.map(l => (
          <div key={l.id} className="list-item" onClick={() => setSelected(l)}>
            <div style={{ width: 38, height: 38, borderRadius: 8, background: 'var(--navy-600)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
              {STATUS_ICON[l.status]}
            </div>
            <div className="list-item-content">
              <div className="list-item-title">{l.judul}</div>
              <div className="list-item-sub">{l.pembuat_name} · {l.wilayah_nama}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 2 }}>{formatDateTime(l.created_at)}</div>
            </div>
            <div className="list-item-right">
              <span className={`badge ${STATUS_COLOR[l.status]}`}>{STATUS_LABEL[l.status]}</span>
              {l.sla_menit && <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{formatSLA(l.sla_menit)}</span>}
            </div>
          </div>
        ))
      }

      {/* Detail Modal */}
      {selected && selectedDetail && (
        <div className="modal-overlay" onClick={() => { setSelected(null); setSelectedDetail(null); }}>
          <div className="modal-sheet" style={{ maxHeight: '90vh' }} onClick={e => e.stopPropagation()}
            onTouchStart={e => { e._startY = e.touches[0].clientY; }}
            onTouchEnd={e => {
              const dy = e.changedTouches[0].clientY - e._startY;
              if (dy > 80) { setSelected(null); setSelectedDetail(null); }
            }}>
            <div className="modal-handle" style={{ cursor: 'grab' }} />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h2 className="modal-title" style={{ marginBottom: 0, flex: 1, paddingRight: 8 }}>{selectedDetail.judul}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`badge ${STATUS_COLOR[selectedDetail.status]}`}>{STATUS_LABEL[selectedDetail.status]}</span>
                <button onClick={() => { setSelected(null); setSelectedDetail(null); }} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: '#8899aa', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>✕</button>
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>
              {selectedDetail.pembuat_name} · {selectedDetail.wilayah_nama} · {formatDateTime(selectedDetail.created_at)}
            </div>

            {/* SLA Box */}
            <div style={{ background: 'var(--navy-900)', border: '1px solid var(--border-hover)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>SLA</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--gold-400)', marginTop: 2 }}>
                {selectedDetail.sla_menit ? formatSLA(selectedDetail.sla_menit) : 'Sedang berjalan...'}
              </div>
              {selectedDetail.selesai_by_name && <div style={{ fontSize: '0.75rem', color: 'var(--green)', marginTop: 4 }}>✅ Diselesaikan oleh <strong>{selectedDetail.selesai_by_name}</strong></div>}
              {selectedDetail.diproses_by_name && <div style={{ fontSize: '0.75rem', color: 'var(--yellow)', marginTop: 2 }}>⚙️ Diproses oleh <strong>{selectedDetail.diproses_by_name}</strong></div>}
              {selectedDetail.ditinjau_by_name && <div style={{ fontSize: '0.75rem', color: 'var(--blue)', marginTop: 2 }}>🔍 Ditinjau oleh <strong>{selectedDetail.ditinjau_by_name}</strong></div>}
            </div>

            {/* Isi */}
            <p style={{ fontSize: '0.9rem', lineHeight: 1.7, marginBottom: 12, color: 'var(--text-primary)' }}>{selectedDetail.isi}</p>

            {/* Foto awal */}
            {(Array.isArray(selectedDetail.foto_urls) ? selectedDetail.foto_urls : JSON.parse(selectedDetail.foto_urls || '[]')).length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                {(Array.isArray(selectedDetail.foto_urls) ? selectedDetail.foto_urls : JSON.parse(selectedDetail.foto_urls || '[]')).map((url, i) => (
                  <img key={i} src={url} alt="" onClick={() => setLightbox(url)}
                    style={{ width: 70, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} />
                ))}
              </div>
            )}

            {/* Tombol update status */}
            {selectedDetail.status !== 'selesai' && nextStatus(selectedDetail.status) && (
              <button className="btn btn-gold btn-full" style={{ marginBottom: 12 }}
                onClick={() => startStatusChange(nextStatus(selectedDetail.status))}>
                {STATUS_ICON[nextStatus(selectedDetail.status)]} Ubah ke {STATUS_LABEL[nextStatus(selectedDetail.status)]}
              </button>
            )}

            {/* Download PDF */}
            <button className="btn btn-outline btn-full" style={{ marginBottom: 12 }} onClick={() => downloadPDF(selectedDetail)}>
              🖨️ Download PDF
            </button>

            <div className="divider" />

            {/* Riwayat / Audit Trail */}
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>RIWAYAT PENANGANAN</div>
            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 12 }}>
              {(selectedDetail.history || []).map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, position: 'relative' }}>
                  {i < (selectedDetail.history.length - 1) && (
                    <div style={{ position: 'absolute', left: 13, top: 28, bottom: -14, width: 2, background: 'var(--border)' }} />
                  )}
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--navy-600)', border: '1px solid var(--border-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', flexShrink: 0, zIndex: 1 }}>
                    {STATUS_ICON[h.status_to] || '📝'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                      {h.status_from ? `${STATUS_LABEL[h.status_from]} → ` : ''}<span style={{ color: 'var(--gold-400)' }}>{STATUS_LABEL[h.status_to]}</span>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>oleh <strong style={{ color: 'var(--text-secondary)' }}>{h.user_name}</strong> · {formatDateTime(h.created_at)}</div>
                    {h.catatan && <div style={{ fontSize: '0.78rem', marginTop: 4, color: 'var(--text-primary)' }}>{h.catatan}</div>}
                    {h.foto_url && (
                      <img src={h.foto_url} alt="bukti" onClick={() => setLightbox(h.foto_url)}
                        style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6, marginTop: 6, border: '1px solid var(--border)', cursor: 'pointer' }} />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Komentar */}
            <div className="divider" />
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>KOMENTAR ({komentar.length})</div>
            {komentar.length > 0 && (
              <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 12 }}>
                {komentar.map((k, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--navy-600)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--gold-400)', flexShrink: 0, overflow: 'hidden' }}>
                      {k.user_foto ? <img src={k.user_foto} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : getInitials(k.user_name)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 600 }}>{k.user_name} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {formatDateTime(k.created_at)}</span></div>
                      <div style={{ fontSize: '0.85rem', marginTop: 2 }}>{k.isi}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="input" style={{ flex: 1 }} placeholder="Tulis komentar..." value={newKomen}
                onChange={e => setNewKomen(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendKomen()} />
              <button className="btn btn-gold" onClick={sendKomen}>Kirim</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Update Status */}
      {showStatusModal && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">{STATUS_ICON[statusTarget]} Ubah Status ke {STATUS_LABEL[statusTarget]}</h2>

            {['diproses', 'selesai'].includes(statusTarget) && (
              <div style={{ background: 'var(--yellow-soft)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', color: 'var(--yellow)', marginBottom: 16 }}>
                📸 Foto bukti wajib untuk status ini
              </div>
            )}

            {/* Preview foto */}
            {statusFotoPreview && (
              <div style={{ marginBottom: 12 }}>
                <img src={statusFotoPreview} alt="preview" style={{ width: '100%', borderRadius: 10, maxHeight: 180, objectFit: 'cover' }} />
              </div>
            )}

            {!cameraMode && (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button className="btn btn-outline btn-full btn-sm" onClick={startCamera}>📷 Kamera</button>
                  <label className="btn btn-outline btn-full btn-sm" style={{ cursor: 'pointer' }}>
                    🖼️ Galeri
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleStatusFotoFile} />
                  </label>
                </div>
                <div className="input-group">
                  <label className="input-label">Catatan (opsional)</label>
                  <textarea className="input" rows={2} placeholder="Keterangan perubahan status..." value={statusCatatan}
                    onChange={e => setStatusCatatan(e.target.value)} style={{ resize: 'none' }} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button className="btn btn-ghost btn-full" onClick={() => setShowStatusModal(false)}>Batal</button>
                  <button className="btn btn-gold btn-full" onClick={submitStatusChange} disabled={saving}>
                    {saving ? '...' : 'Konfirmasi'}
                  </button>
                </div>
              </>
            )}

            {cameraMode && (
              <>
                <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 12, aspectRatio: '4/3', background: '#000' }}>
                  <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost btn-full" onClick={stopCamera}>Batal</button>
                  <button className="btn btn-gold btn-full" onClick={capturePhoto}>📸 Ambil Foto</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Buat Laporan */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Buat Laporan</h2>
            <div className="input-group">
              <label className="input-label">Judul</label>
              <input className="input" placeholder="Judul laporan..." value={form.judul} onChange={e => setForm(f => ({ ...f, judul: e.target.value }))} />
            </div>
            <div className="input-group">
              <label className="input-label">Kategori</label>
              <select className="input" value={form.kategori} onChange={e => setForm(f => ({ ...f, kategori: e.target.value }))}>
                {KATEGORI.map(k => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Isi Laporan</label>
              <textarea className="input" rows={4} placeholder="Deskripsikan kejadian secara detail..." value={form.isi}
                onChange={e => setForm(f => ({ ...f, isi: e.target.value }))} style={{ resize: 'none' }} />
            </div>
            <div className="input-group">
              <label className="input-label">Foto Bukti (maks 5)</label>
              <input type="file" accept="image/*" multiple className="input" onChange={e => setFotos(Array.from(e.target.files).slice(0, 5))} />
              {fotos.length > 0 && <div style={{ fontSize: '0.75rem', color: 'var(--green)', marginTop: 4 }}>{fotos.length} foto dipilih</div>}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowForm(false)}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={submitLaporan} disabled={saving}>{saving ? '...' : 'Kirim Laporan'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
